import { useState } from 'react'
import type { Tab } from '../lib/types'
import {
  formatMoney,
  formatPercent,
  formatShortDate,
  formatWeekLabel,
  tabPayTotalCents,
} from '../lib/money'
import { groupTabsByWeek, summarizeTabs } from '../lib/roasts'
import { shareWeeklyCard } from '../lib/shareCard'
import { weeklyDamageReport } from '../lib/weeklySummary'

type Props = {
  tabs: Tab[]
  onDelete: (id: string) => void
}

function kindLabel(kind: Tab['kind']): string {
  if (kind === 'bar') return 'Bar'
  if (kind === 'restaurant') return 'Restaurant'
  return 'Other'
}

function formatAutoGrat(tab: Tab): string {
  const cents = tab.autoGratCents ?? 0
  if (cents <= 0) return ''
  if (tab.autoGratPercent != null) {
    return ` + ${formatMoney(cents)} auto-grat (${formatPercent(tab.autoGratPercent)})`
  }
  return ` + ${formatMoney(cents)} auto-grat`
}

export function TabHistory({ tabs, onDelete }: Props) {
  const weeks = groupTabsByWeek(tabs)
  const month = summarizeTabs(tabs)
  const weekReport = weeklyDamageReport(tabs)
  const [sharingWeek, setSharingWeek] = useState(false)

  const onShareWeek = async () => {
    if (!weekReport || sharingWeek) return
    setSharingWeek(true)
    try {
      await shareWeeklyCard(weekReport)
    } finally {
      setSharingWeek(false)
    }
  }

  if (tabs.length === 0) {
    return (
      <section className="card">
        <p className="eyebrow">Last few weeks</p>
        <p className="mt-2 text-sm text-foam-muted">
          Nothing logged yet. Save a tab and we will keep the last twelve weeks on this phone.
        </p>
      </section>
    )
  }

  return (
    <section className="space-y-3">
      <div className="card">
        <p className="eyebrow">Last few weeks</p>
        {weekReport ? (
          <p className="mt-1 text-xs leading-relaxed text-foam-muted">
            Last 7 days: {weekReport.visitCount === 1 ? '1 visit' : `${weekReport.visitCount} visits`}{' '}
            · {formatMoney(weekReport.totalCents)} · top: {weekReport.topVenue}
          </p>
        ) : null}
        <div className="mt-3 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-lg font-bold">{formatMoney(month.totalCents)}</p>
            <p className="text-[11px] text-foam-faint">spent</p>
          </div>
          <div>
            <p className="text-lg font-bold text-whiskey-light">{formatMoney(month.tipCents)}</p>
            <p className="text-[11px] text-foam-faint">tipped</p>
          </div>
          <div>
            <p className="text-lg font-bold">{formatPercent(month.averageTipPercent)}</p>
            <p className="text-[11px] text-foam-faint">avg tip</p>
          </div>
        </div>
        {weekReport ? (
          <button
            type="button"
            className="secondary mt-4 min-h-11 w-full text-sm"
            disabled={sharingWeek}
            onClick={() => void onShareWeek()}
          >
            {sharingWeek ? 'Preparing…' : 'Share weekly damage'}
          </button>
        ) : null}
      </div>

      {weeks.map((week) => (
        <div key={week.weekStart.toISOString()} className="card space-y-3">
          <div className="flex items-end justify-between gap-2">
            <div>
              <p className="text-sm font-bold">{formatWeekLabel(week.weekStart)}</p>
              <p className="text-xs text-foam-faint">
                {week.tabs.length === 1 ? '1 tab' : `${week.tabs.length} tabs`} ·{' '}
                {formatMoney(week.totalCents)}
              </p>
            </div>
            <p className="text-xs font-semibold text-whiskey">{formatPercent(week.averageTipPercent)}</p>
          </div>
          <ul className="space-y-2">
            {week.tabs.map((tab) => (
              <li
                key={tab.id}
                className="flex items-start justify-between gap-3 rounded-xl border border-line bg-bar px-3 py-3"
              >
                <div className="min-w-0">
                  <p className="truncate font-semibold">
                    {tab.venue.trim() || 'Unnamed watering hole'}
                  </p>
                  <p className="text-xs text-foam-faint">
                    {formatShortDate(tab.date)} · {kindLabel(tab.kind)}
                    {tab.source === 'scan' ? ' · scanned' : ''}
                  </p>
                  <p className="mt-1 text-sm text-foam-muted">
                    {formatMoney(tab.billCents)}
                    {formatAutoGrat(tab)}{' '}
                    + {formatMoney(tab.tipCents)} tip ({formatPercent(tab.tipPercent)})
                  </p>
                  <p className="text-sm font-semibold text-foam">
                    Total{' '}
                    {formatMoney(
                      tabPayTotalCents(tab.billCents, tab.tipCents, tab.autoGratCents ?? 0),
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-xs text-danger"
                  aria-label={`Delete tab at ${tab.venue.trim() || 'unnamed venue'}`}
                  onClick={() => onDelete(tab.id)}
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  )
}
